import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef } from "react";
import * as THREE from "three";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { useObserverStore } from "../../stores/observerStore";

/** Smoothed camera state readable by other components (parallax, nebula, etc.) */
export const smoothedCameraRef = { current: { x: 0, y: 0, zoom: 1, initialized: false } };

/** Entry animation progress (0 → 1). Read by GalaxyStars for distance-based fade-in. */
export const entryAnimationRef = { current: { progress: 1, active: false } };

const ENTRY_DURATION = 2500; // 2.5 seconds
const ENTRY_START_ZOOM = 0.3; // 30%
const ENTRY_END_ZOOM = 1.5; // 150%

/** Max px movement to count as a click rather than drag */
const CLICK_THRESHOLD = 5;
/** Larger threshold for touch — fingers are imprecise */
const TOUCH_CLICK_THRESHOLD = 10;
/** Snap threshold - max distance from center to trigger snap (in touch radius units) */
const SNAP_THRESHOLD_MULTIPLIER = 2;
/** Base scale for galaxy coordinates */
const BASE_SCALE = 2;
/** Touch hit radius for detection */
const HIT_RADIUS_TOUCH = 40;

interface PointerState {
	x: number;
	y: number;
	type: string;
}

interface PinchState {
	dist: number;
	centerX: number;
	centerY: number;
}

/**
 * Syncs the Three.js OrthographicCamera with the Zustand ViewState.
 * Handles wheel zoom, pinch-to-zoom, drag pan, click detection, and hover cursor.
 */
export function useViewportSync(): void {
	const { camera, gl, size } = useThree();
	const dragRef = useRef<{
		x: number;
		y: number;
		dragging: boolean;
		pointerType: string;
	} | null>(null);

	const pointersRef = useRef<Map<number, PointerState>>(new Map());
	const pinchRef = useRef<PinchState | null>(null);
	const snapAnimationRef = useRef<{
		startTime: number;
		startOffset: { x: number; y: number };
		targetOffset: { x: number; y: number };
	} | null>(null);
	const entryStartRef = useRef<number | null>(null);

	const zoom = useObserverStore((s) => s.zoom);
	const setView = useObserverStore((s) => s.setView);
	const { isMobile } = useDeviceCapabilities();

	/** Smoothing factor: 1.0 = instant (no smoothing), lower = smoother */
	const LERP_FACTOR = 0.12;

	// --- Sync camera from store each frame ---
	useFrame(() => {
		const state = useObserverStore.getState();
		const view = state.viewMode === "galaxy" ? state.galaxyView : state.systemView;
		const cam = camera as THREE.OrthographicCamera;

		// Handle snap animation
		if (snapAnimationRef.current) {
			const elapsed = Date.now() - snapAnimationRef.current.startTime;
			const duration = 300; // 300ms snap animation
			const t = Math.min(elapsed / duration, 1);
			// Ease out cubic
			const eased = 1 - (1 - t) ** 3;

			const { startOffset, targetOffset } = snapAnimationRef.current;
			const currentOffsetX = startOffset.x + (targetOffset.x - startOffset.x) * eased;
			const currentOffsetY = startOffset.y + (targetOffset.y - startOffset.y) * eased;

			setView({ offsetX: currentOffsetX, offsetY: currentOffsetY });

			if (t >= 1) {
				snapAnimationRef.current = null;
			}
		}

		const targetX = (size.width / 2 - view.offsetX) / view.scale;
		const targetY = -(size.height / 2 - view.offsetY) / view.scale;
		const targetZoom = view.scale;

		// --- Entry animation: zoom from 30% → 1000% on first load ---
		const isEntryAnimating = !state.hasEnteredGalaxy && state.viewMode === "galaxy";
		if (isEntryAnimating) {
			if (entryStartRef.current === null) {
				entryStartRef.current = Date.now();
				entryAnimationRef.current.active = true;
				entryAnimationRef.current.progress = 0;
			}
			const elapsed = Date.now() - entryStartRef.current;
			const t = Math.min(elapsed / ENTRY_DURATION, 1);
			// Ease out quart for smooth deceleration
			const eased = 1 - (1 - t) ** 4;

			entryAnimationRef.current.progress = eased;

			// Override zoom: sweep from ENTRY_START_ZOOM (0.3) → ENTRY_END_ZOOM (10)
			const entryZoom = THREE.MathUtils.lerp(ENTRY_START_ZOOM, ENTRY_END_ZOOM, eased);

			// Compute camera position at the animated zoom level
			const entryTargetX = (size.width / 2 - view.offsetX) / entryZoom;
			const entryTargetY = -(size.height / 2 - view.offsetY) / entryZoom;

			smoothedCameraRef.current.x = entryTargetX;
			smoothedCameraRef.current.y = entryTargetY;
			smoothedCameraRef.current.zoom = entryZoom;
			smoothedCameraRef.current.initialized = true;

			if (t >= 1) {
				entryAnimationRef.current.active = false;
				entryAnimationRef.current.progress = 1;
				// Set the final scale in the store so it persists
				setView({ scale: ENTRY_END_ZOOM });
				state.setHasEnteredGalaxy(true);
			}
		} else {
			// Normal smoothing
			const isDragging = dragRef.current?.dragging === true || pinchRef.current !== null;
			if (!smoothedCameraRef.current.initialized) {
				smoothedCameraRef.current.x = targetX;
				smoothedCameraRef.current.y = targetY;
				smoothedCameraRef.current.zoom = targetZoom;
				smoothedCameraRef.current.initialized = true;
			} else {
				const factor = isDragging ? 1.0 : LERP_FACTOR;
				smoothedCameraRef.current.x = THREE.MathUtils.lerp(
					smoothedCameraRef.current.x,
					targetX,
					factor,
				);
				smoothedCameraRef.current.y = THREE.MathUtils.lerp(
					smoothedCameraRef.current.y,
					targetY,
					factor,
				);
				smoothedCameraRef.current.zoom = THREE.MathUtils.lerp(
					smoothedCameraRef.current.zoom,
					targetZoom,
					factor,
				);
			}
		}

		cam.position.x = smoothedCameraRef.current.x;
		cam.position.y = smoothedCameraRef.current.y;
		cam.zoom = smoothedCameraRef.current.zoom;
		cam.updateProjectionMatrix();
	});

	// --- Wheel zoom ---
	const handleWheel = useCallback(
		(e: WheelEvent) => {
			e.preventDefault();
			const rect = gl.domElement.getBoundingClientRect();
			const pointerX = e.clientX - rect.left;
			const pointerY = e.clientY - rect.top;
			const scaleBy = 1.1;
			const factor = e.deltaY > 0 ? 1 / scaleBy : scaleBy;
			zoom(factor, pointerX, pointerY);
		},
		[gl.domElement, zoom],
	);

	// --- Pointer down ---
	const handlePointerDown = useCallback(
		(e: PointerEvent) => {
			pointersRef.current.set(e.pointerId, {
				x: e.clientX,
				y: e.clientY,
				type: e.pointerType,
			});

			// Only start single-pointer drag if this is the first/only pointer
			if (pointersRef.current.size === 1) {
				dragRef.current = {
					x: e.clientX,
					y: e.clientY,
					dragging: false,
					pointerType: e.pointerType,
				};
			}

			gl.domElement.setPointerCapture(e.pointerId);
		},
		[gl.domElement],
	);

	// --- Pointer move ---
	const handlePointerMove = useCallback(
		(e: PointerEvent) => {
			// Update tracked pointer position
			if (pointersRef.current.has(e.pointerId)) {
				pointersRef.current.set(e.pointerId, {
					x: e.clientX,
					y: e.clientY,
					type: e.pointerType,
				});
			}

			// --- Pinch-to-zoom (two fingers) ---
			if (pointersRef.current.size === 2) {
				const pointers = [...pointersRef.current.values()];
				const p1 = pointers[0];
				const p2 = pointers[1];
				if (!p1 || !p2) return;
				const currentDist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
				const centerX = (p1.x + p2.x) / 2;
				const centerY = (p1.y + p2.y) / 2;

				if (pinchRef.current) {
					// Zoom
					const factor = currentDist / pinchRef.current.dist;
					const rect = gl.domElement.getBoundingClientRect();
					zoom(factor, centerX - rect.left, centerY - rect.top);

					// Two-finger pan
					const dx = centerX - pinchRef.current.centerX;
					const dy = centerY - pinchRef.current.centerY;
					if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
						const state = useObserverStore.getState();
						const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
						const current = state[key];
						setView({
							offsetX: current.offsetX + dx,
							offsetY: current.offsetY + dy,
						});
					}
				}

				pinchRef.current = { dist: currentDist, centerX, centerY };

				// Cancel any single-pointer drag — we're in pinch mode
				if (dragRef.current) {
					dragRef.current.dragging = true; // mark as dragged so no click fires
				}
				return;
			}

			// --- Single-pointer drag ---
			if (!dragRef.current) return;

			const dx = e.clientX - dragRef.current.x;
			const dy = e.clientY - dragRef.current.y;
			const dist = Math.hypot(dx, dy);
			const threshold =
				dragRef.current.pointerType === "touch" ? TOUCH_CLICK_THRESHOLD : CLICK_THRESHOLD;

			if (!dragRef.current.dragging && dist > threshold) {
				dragRef.current.dragging = true;
			}

			if (dragRef.current.dragging) {
				const state = useObserverStore.getState();
				const key = state.viewMode === "galaxy" ? "galaxyView" : "systemView";
				const current = state[key];
				setView({
					offsetX: current.offsetX + dx,
					offsetY: current.offsetY + dy,
				});
				dragRef.current.x = e.clientX;
				dragRef.current.y = e.clientY;
			}
		},
		[gl.domElement, zoom, setView],
	);

	// --- Pointer up ---
	const handlePointerUp = useCallback(
		(e: PointerEvent) => {
			pointersRef.current.delete(e.pointerId);

			// Reset pinch state when fewer than 2 pointers remain
			if (pointersRef.current.size < 2) {
				pinchRef.current = null;
			}

			const drag = dragRef.current;
			dragRef.current = null;

			try {
				gl.domElement.releasePointerCapture(e.pointerId);
			} catch {
				// Pointer capture may already be released
			}

			if (!drag) return;

			// If it was a click (not a drag), dispatch for hit testing
			if (!drag.dragging) {
				const rect = gl.domElement.getBoundingClientRect();
				const screenX = e.clientX - rect.left;
				const screenY = e.clientY - rect.top;

				gl.domElement.dispatchEvent(
					new CustomEvent("canvas-click", {
						detail: { screenX, screenY, pointerType: e.pointerType },
					}),
				);
			} else if (isMobile && e.pointerType === "touch" && drag.dragging) {
				// Mobile: snap to nearest star on drag release
				const state = useObserverStore.getState();
				if (state.viewMode !== "galaxy") return;

				const rect = gl.domElement.getBoundingClientRect();
				const centerScreenX = rect.width / 2;
				const centerScreenY = rect.height / 2;

				const { worldX, worldY } = screenToWorld(centerScreenX, centerScreenY);

				const maxDistance = (HIT_RADIUS_TOUCH * SNAP_THRESHOLD_MULTIPLIER) / state.galaxyView.scale;
				let nearest: { x: number; y: number } | null = null;
				let minDist = maxDistance;

				for (const system of state.systems) {
					const px = system.x * BASE_SCALE;
					const py = system.y * BASE_SCALE;
					const dist = Math.hypot(px - worldX, py - worldY);
					if (dist < minDist) {
						minDist = dist;
						nearest = { x: px, y: py };
					}
				}

				// If we found a nearby star, snap to it
				if (nearest) {
					const targetWorldX = nearest.x;
					const targetWorldY = nearest.y;

					// Calculate what offsetX/offsetY should be to center this star
					const targetOffsetX = centerScreenX - targetWorldX * state.galaxyView.scale;
					const targetOffsetY = centerScreenY - targetWorldY * state.galaxyView.scale;

					snapAnimationRef.current = {
						startTime: Date.now(),
						startOffset: { x: state.galaxyView.offsetX, y: state.galaxyView.offsetY },
						targetOffset: { x: targetOffsetX, y: targetOffsetY },
					};
				}
			}
		},
		[gl.domElement, isMobile],
	);

	// --- Pointer cancel (touch interrupted) ---
	const handlePointerCancel = useCallback(
		(e: PointerEvent) => {
			pointersRef.current.delete(e.pointerId);
			if (pointersRef.current.size < 2) {
				pinchRef.current = null;
			}
			dragRef.current = null;
			try {
				gl.domElement.releasePointerCapture(e.pointerId);
			} catch {
				// Already released
			}
		},
		[gl.domElement],
	);

	// --- touch-action + cursor ---
	useEffect(() => {
		const el = gl.domElement;
		el.style.touchAction = "none";
		el.style.cursor = "grab";
		const setGrabbing = () => {
			el.style.cursor = "grabbing";
		};
		const setGrab = () => {
			el.style.cursor = "grab";
		};
		el.addEventListener("pointerdown", setGrabbing);
		el.addEventListener("pointerup", setGrab);
		return () => {
			el.removeEventListener("pointerdown", setGrabbing);
			el.removeEventListener("pointerup", setGrab);
		};
	}, [gl.domElement]);

	// --- Attach events ---
	useEffect(() => {
		const el = gl.domElement;
		el.addEventListener("wheel", handleWheel, { passive: false });
		el.addEventListener("pointerdown", handlePointerDown);
		el.addEventListener("pointermove", handlePointerMove);
		el.addEventListener("pointerup", handlePointerUp);
		el.addEventListener("pointercancel", handlePointerCancel);
		return () => {
			el.removeEventListener("wheel", handleWheel);
			el.removeEventListener("pointerdown", handlePointerDown);
			el.removeEventListener("pointermove", handlePointerMove);
			el.removeEventListener("pointerup", handlePointerUp);
			el.removeEventListener("pointercancel", handlePointerCancel);
		};
	}, [
		gl.domElement,
		handleWheel,
		handlePointerDown,
		handlePointerMove,
		handlePointerUp,
		handlePointerCancel,
	]);
}

/** Convert screen pixel coords to world coords using current store ViewState */
export function screenToWorld(
	screenX: number,
	screenY: number,
): { worldX: number; worldY: number } {
	const state = useObserverStore.getState();
	const view = state.viewMode === "galaxy" ? state.galaxyView : state.systemView;
	const worldX = (screenX - view.offsetX) / view.scale;
	const worldY = (screenY - view.offsetY) / view.scale;
	return { worldX, worldY };
}
